/**
 * ChatSettings - Modern Chat Configuration Panel
 * 
 * Features:
 * - Clean tabbed interface
 * - Model selection with info cards
 * - System instructions
 * - Tools & MCP management
 * - API keys configuration
 * - Sync settings
 */

import { api } from "@acme/client"
import {
    Button,
    IoMdWarning,
    PopupWrapperAnimated,
    BsGear,
    BsRobot,
    BsTools,
    BsGlobe,
    BsKey,
    BsCloudArrowUp,
    BiChevronRight,
    BsCheck2Circle,
    BsXCircle,
    BiX,
    toast,
    ModelPicker,
} from "@acme/ui"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { memo, useCallback, useEffect, useState } from "react"
import { chatSessionActiveAtom, chatSessionsSettingsAtom } from "../store/chat"
import { useUserSubscription } from "../hooks"
import {
    modelPopupAtom,
    popupSettingsInstructionTriggerAtom,
    globalToolsConfigAtom,
    mcpServersConfigAtom,
} from "../store/instruction"
import {
    type ChatModel,
    defaultModelPrismaToString,
    messageToChatMessage,
    getModelConfig,
    toolMetadata,
    type ToolName,
    DEFAULT_MODEL,
} from "@acme/util"
import { teamActiveAtom, upgradeModalAtom } from "../store/user"

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ChatSettings = memo(() => {
    const [isOpen, setIsOpen] = useAtom(chatSessionsSettingsAtom)
    if (!isOpen) return null
    return (
        <ChatSettingsComponent onClose={() => setIsOpen(false)} />
    )
})

ChatSettings.displayName = 'ChatSettings'

// =============================================================================
// SETTINGS COMPONENT
// =============================================================================

type SettingsTab = 'general' | 'tools' | 'advanced'

export const ChatSettingsComponent = ({ onClose }: { onClose: () => void }) => {
    const teamActive = useAtomValue(teamActiveAtom)
    const [chatSession, setChatSession] = useAtom(chatSessionActiveAtom)
    const [activeTab, setActiveTab] = useState<SettingsTab>('general')
    const [tempValue, setTempValue] = useState({
        name: chatSession?.name || '',
        defaultInstruction: chatSession?.defaultInstruction || '',
        isSync: chatSession?.isSync || false,
        defaultModel: chatSession?.defaultModel || DEFAULT_MODEL,
        isStreaming: chatSession?.isStreaming ?? true,
    })
    const [insSettingsTrigger, setInsSettingsTrigger] = useAtom(popupSettingsInstructionTriggerAtom)

    const { mutateAsync: mutateUpdateChat, isLoading } = api.chat.updateChatSession.useMutation({
        onSuccess: (data) => {
            if (data && chatSession) {
                setChatSession({
                    id: chatSession.id,
                    name: tempValue.name,
                    defaultInstruction: tempValue.defaultInstruction,
                    defaultModel: tempValue.defaultModel,
                    isStreaming: tempValue.isStreaming,
                    isSync: true,
                    isInit: false
                })
                onClose()
            }
        }
    })

    const { mutateAsync: mutateCreate, isLoading: isLoadingCreate } = api.chat.createChatSession.useMutation({
        onSuccess: (data) => {
            if (data && chatSession) {
                setChatSession({
                    ...chatSession,
                    ...(data?.messages?.length && { messages: data.messages.map(m => messageToChatMessage(m)) }),
                    id: data.id,
                    name: data.name || tempValue.name,
                    isStreaming: data.isStreaming ?? tempValue.isStreaming,
                    defaultInstruction: data.defaultInstruction || tempValue.defaultInstruction,
                    defaultModel: data.defaultModel ? defaultModelPrismaToString(data.defaultModel) : tempValue.defaultModel || DEFAULT_MODEL,
                    isSync: true,
                    isInit: false
                })
                onClose()
            }
        }
    })

    const { mutateAsync: mutateDelete, isLoading: isLoadingDelete } = api.chat.deleteChatSession.useMutation()

    const onSave = async () => {
        if (!chatSession) return

        if (chatSession?.isSync && !chatSession?.isInit && !tempValue.isSync) {
            await mutateDelete({ id: chatSession.id })
            setChatSession({
                id: chatSession.id,
                name: tempValue.name,
                defaultInstruction: tempValue.defaultInstruction,
                defaultModel: tempValue.defaultModel,
                isStreaming: tempValue.isStreaming,
                isSync: false,
                updated: new Date(),
            })
        } else if (chatSession.isSync && !chatSession.isInit) {
            await mutateUpdateChat({
                id: chatSession.id,
                name: tempValue.name,
                defaultInstruction: tempValue.defaultInstruction,
                defaultModel: tempValue.defaultModel,
                isStreaming: tempValue.isStreaming,
            })
        } else if (tempValue.isSync && teamActive?.id) {
            await mutateCreate({
                localId: chatSession.id,
                name: tempValue.name,
                teamId: teamActive?.id,
                defaultInstruction: tempValue.defaultInstruction,
                defaultModel: tempValue.defaultModel,
                isStreaming: tempValue.isStreaming,
                messages: chatSession.messages.map(m => ({
                    ...m,
                    localId: m.id,
                    chatSessionId: chatSession.id,
                })),
                order: chatSession.order || 100,
                orderUpdated: chatSession?.orderUpdated || new Date(),
            })
        } else {
            setChatSession({
                id: chatSession.id,
                name: tempValue.name,
                defaultInstruction: tempValue.defaultInstruction,
                defaultModel: tempValue.defaultModel,
                isStreaming: tempValue.isStreaming,
            })
        }
        onClose()
    }

    useEffect(() => {
        if (chatSession && chatSession.name && chatSession.isSync) {
            setTempValue({
                name: chatSession.name,
                defaultInstruction: chatSession.defaultInstruction || '',
                isSync: chatSession.isSync,
                defaultModel: chatSession.defaultModel || DEFAULT_MODEL,
                isStreaming: chatSession.isStreaming ?? true,
            })
        }
    }, [chatSession])

    useEffect(() => {
        if (insSettingsTrigger.type === 'chat') {
            setTempValue(prev => ({ ...prev, defaultInstruction: insSettingsTrigger.instruction || '' }))
            setInsSettingsTrigger({})
        }
    }, [insSettingsTrigger, setInsSettingsTrigger])

    const modelConfig = getModelConfig(tempValue.defaultModel)

    const tabs: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
        { id: 'general', label: 'General', icon: <BsGear className="text-sm" /> },
        { id: 'tools', label: 'Tools & MCP', icon: <BsTools className="text-sm" /> },
        { id: 'advanced', label: 'Advanced', icon: <BsKey className="text-sm" /> },
    ]

    return (
        <PopupWrapperAnimated className="!bg-transparent !p-0 !shadow-none" onClose={onClose} show noClose>
            <div className="w-full max-w-3xl bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-neutral-700">
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                                <BsGear className="text-white text-lg" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    Chat Settings
                                </h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Configure your chat experience
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
                        >
                            <BiX className="text-xl text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-neutral-700 px-6">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.id
                                ? 'text-blue-600 dark:text-blue-400'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                        >
                            {tab.icon}
                            {tab.label}
                            {activeTab === tab.id && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full" />
                            )}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                    {activeTab === 'general' && (
                        <GeneralTab
                            tempValue={tempValue}
                            setTempValue={setTempValue}
                            modelConfig={modelConfig}
                        />
                    )}
                    {activeTab === 'tools' && <ToolsTab />}
                    {activeTab === 'advanced' && <AdvancedTab />}
                </div>

                {/* Unsync Warning */}
                {(chatSession?.isSync && !chatSession?.isInit && !tempValue?.isSync) && (
                    <div className="mx-6 mb-4 p-3 flex flex-row gap-3 items-start bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                        <IoMdWarning className="text-red-500 text-xl flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-red-700 dark:text-red-300">
                            <p className="font-medium">Warning: This will remove cloud sync</p>
                            <p className="text-red-600 dark:text-red-400 mt-1 text-xs">
                                Your chat data will be deleted from the server. This action cannot be undone.
                            </p>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800/50 flex justify-between items-center">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <Button
                        loading={isLoading || isLoadingCreate || isLoadingDelete}
                        className="!bg-blue-600 hover:!bg-blue-700 !text-white !py-2 !px-6 !rounded-xl"
                        onClick={() => void onSave()}
                    >
                        Save Changes
                    </Button>
                </div>
            </div>
        </PopupWrapperAnimated>
    )
}

// =============================================================================
// GENERAL TAB
// =============================================================================

interface TempValueType {
    name: string
    defaultInstruction: string
    defaultModel: ChatModel
    isStreaming: boolean
    isSync: boolean
}

interface GeneralTabProps {
    tempValue: TempValueType
    setTempValue: React.Dispatch<React.SetStateAction<TempValueType>>
    modelConfig: ReturnType<typeof getModelConfig>
}

const GeneralTab = memo(({ tempValue, setTempValue, modelConfig }: GeneralTabProps) => {
    const setModelPopup = useSetAtom(modelPopupAtom)
    return (
        <div className="space-y-6">
            {/* Chat Name */}
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Chat Name
                </label>
                <input
                    type="text"
                    value={tempValue.name}
                    onChange={(e) => setTempValue(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Project Ideas, Research Notes..."
                    className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
            </div>

            {/* Model Selection */}
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    AI Model
                </label>
                <ModelPicker
                    value={tempValue.defaultModel}
                    onChange={(model) => setTempValue(prev => ({ ...prev, defaultModel: model }))}
                />

                {/* Model Info Card */}
                {modelConfig && (
                    <div className="mt-3 p-4 rounded-xl bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700">
                        <div className="flex items-center gap-2 mb-2">
                            <BsRobot className="text-blue-600 dark:text-blue-400" />
                            <span className="font-semibold text-gray-900 dark:text-white">{modelConfig.displayName}</span>
                            {modelConfig.isReasoning && (
                                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full text-xs">
                                    Reasoning
                                </span>
                            )}
                        </div>
                        <div className="flex gap-4 text-xs text-gray-600 dark:text-gray-400">
                            <span>📝 Context: {(modelConfig.contextWindow / 1000).toFixed(0)}K</span>
                            <span>💬 Output: {(modelConfig.maxOutputTokens / 1000).toFixed(0)}K</span>
                        </div>
                    </div>
                )}
            </div>

            {/* System Instruction */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        System Instruction
                    </label>
                    <button
                        onClick={() => setModelPopup({ open: true, type: 'instruction' })}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                        Browse templates
                    </button>
                </div>
                <textarea
                    value={tempValue.defaultInstruction}
                    onChange={(e) => setTempValue(prev => ({ ...prev, defaultInstruction: e.target.value }))}
                    placeholder="You are a helpful assistant..."
                    rows={4}
                    className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                />
            </div>

            {/* Toggle Options */}
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-neutral-700">
                <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                            <BsRobot className="text-blue-600 dark:text-blue-400 text-sm" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">Stream responses</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">See AI responses in real-time</p>
                        </div>
                    </div>
                    <SwitchToggle
                        value={tempValue.isStreaming}
                        onChange={(e) => setTempValue(prev => ({ ...prev, isStreaming: e }))}
                    />
                </div>

                <ChatSyncToCloudButton
                    value={tempValue.isSync}
                    onChange={(e) => setTempValue(prev => ({ ...prev, isSync: e }))}
                />
            </div>
        </div>
    )
})

GeneralTab.displayName = 'GeneralTab'

// =============================================================================
// TOOLS TAB
// =============================================================================

const ToolsTab = memo(() => {
    const toolsConfig = useAtomValue(globalToolsConfigAtom)
    const mcpServers = useAtomValue(mcpServersConfigAtom)
    const setModelPopup = useSetAtom(modelPopupAtom)

    const enabledToolsArray = toolsConfig.enabledTools
    const enabledCount = enabledToolsArray.length
    const totalTools = Object.keys(toolMetadata).length

    const openToolsConfig = useCallback(() => {
        setModelPopup({ open: true, type: 'tools-config' })
    }, [setModelPopup])

    return (
        <div className="space-y-6">
            {/* Tools Overview Card */}
            <div
                onClick={openToolsConfig}
                className="p-5 rounded-xl bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 cursor-pointer hover:border-gray-300 dark:hover:border-neutral-600 hover:shadow-sm transition-all group"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
                            <BsTools className="text-white text-xl" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white">Built-in Tools</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {enabledCount} of {totalTools} tools enabled
                            </p>
                        </div>
                    </div>
                    <BiChevronRight className="text-2xl text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                </div>

                {/* Quick preview of enabled tools */}
                {enabledCount > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                        {enabledToolsArray.slice(0, 5).map(toolKey => {
                            const meta = toolMetadata[toolKey as ToolName]
                            if (!meta) return null
                            return (
                                <span
                                    key={toolKey}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-full text-gray-700 dark:text-gray-300"
                                >
                                    <span>{meta.icon}</span>
                                    {meta.name}
                                </span>
                            )
                        })}
                        {enabledCount > 5 && (
                            <span className="px-2.5 py-1 text-xs bg-gray-100 dark:bg-neutral-700 rounded-full text-gray-500">
                                +{enabledCount - 5} more
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* MCP Servers Card */}
            <div
                onClick={openToolsConfig}
                className="p-5 rounded-xl bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 cursor-pointer hover:border-gray-300 dark:hover:border-neutral-600 hover:shadow-sm transition-all group"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-neutral-800 dark:bg-neutral-600 flex items-center justify-center">
                            <BsGlobe className="text-white text-xl" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white">MCP Servers</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {mcpServers.length > 0
                                    ? `${mcpServers.filter(s => s.enabled).length} of ${mcpServers.length} servers connected`
                                    : 'Connect external tools and data sources'
                                }
                            </p>
                        </div>
                    </div>
                    <BiChevronRight className="text-2xl text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                </div>

                {/* Quick preview of servers */}
                {mcpServers.length > 0 && (
                    <div className="flex flex-col gap-2 mt-4">
                        {mcpServers.slice(0, 3).map(server => (
                            <div
                                key={server.id}
                                className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg"
                            >
                                {server.enabled ? (
                                    <BsCheck2Circle className="text-green-500 text-sm" />
                                ) : (
                                    <BsXCircle className="text-gray-400 text-sm" />
                                )}
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{server.name}</span>
                                <span className="text-[10px] text-gray-400 font-mono truncate">{server.url}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Info */}
            <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                    💡 <strong>Tip:</strong> Tools allow your AI to search the web, perform calculations, fetch data, and more.
                    MCP servers let you connect custom tools and data sources.
                </p>
            </div>
        </div>
    )
})

ToolsTab.displayName = 'ToolsTab'

// =============================================================================
// ADVANCED TAB
// =============================================================================

const AdvancedTab = memo(() => {
    return (
        <div className="space-y-6">
            {/* API Keys Info */}
            <div className="p-5 rounded-xl bg-gradient-to-r from-gray-50 to-slate-50 dark:from-neutral-800 dark:to-neutral-800 border border-gray-200 dark:border-neutral-700">
                <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-500 to-slate-600 flex items-center justify-center">
                        <BsKey className="text-white text-lg" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">API Keys</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Manage your AI provider API keys
                        </p>
                    </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    API keys are configured globally in the main settings. This ensures your keys are securely stored and used across all chats.
                </p>
                <button className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline">
                    Go to API Settings →
                </button>
            </div>

            {/* Tool API Keys */}
            <div className="p-5 rounded-xl border border-gray-200 dark:border-neutral-700">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Tool API Keys</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Some tools require their own API keys to function:
                </p>

                <div className="space-y-3">
                    <ToolApiKeyRow
                        name="Tavily Search"
                        description="For web search capabilities"
                        configured={false}
                    />
                    <ToolApiKeyRow
                        name="Firecrawl"
                        description="For URL scraping and content extraction"
                        configured={false}
                    />
                </div>

                <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                    Tool API key configuration coming soon. For now, tools will use placeholder responses.
                </p>
            </div>

            {/* Danger Zone */}
            <div className="p-5 rounded-xl border border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-900/10">
                <h3 className="font-semibold text-red-700 dark:text-red-400 mb-3">Danger Zone</h3>
                <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                    Destructive actions that cannot be undone.
                </p>
                <button className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                    Delete this chat
                </button>
            </div>
        </div>
    )
})

AdvancedTab.displayName = 'AdvancedTab'

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const ToolApiKeyRow = memo(({ name, description, configured }: { name: string; description: string; configured: boolean }) => (
    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700">
        <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
        </div>
        <span className={`
            px-2.5 py-1 text-xs font-medium rounded-full
            ${configured
                ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
                : 'bg-gray-100 dark:bg-neutral-700 text-gray-500 dark:text-gray-400'
            }
        `}>
            {configured ? 'Configured' : 'Not set'}
        </span>
    </div>
))

ToolApiKeyRow.displayName = 'ToolApiKeyRow'

const SwitchToggle = memo(({ value, onChange }: { value: boolean; onChange: (val: boolean) => void }) => (
    <button
        onClick={() => onChange(!value)}
        className={`
            relative w-11 h-6 rounded-full transition-colors
            ${value ? 'bg-blue-500' : 'bg-gray-300 dark:bg-neutral-600'}
        `}
    >
        <div className={`
            absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform
            ${value ? 'translate-x-6' : 'translate-x-1'}
        `} />
    </button>
))

SwitchToggle.displayName = 'SwitchToggle'

export const ChatSyncToCloudButton = memo(({ value, onChange, className }: { value: boolean; onChange: (val: boolean) => void; className?: string }) => {
    const { isAllowAddChatSession, isAllowCloud, isSubscribe } = useUserSubscription()
    const setUpgradeModal = useSetAtom(upgradeModalAtom)

    // Hide if user can't sync and it's not already synced
    if (!isAllowAddChatSession?.allowSync && !value) return null

    const handleToggle = (newValue: boolean) => {
        // Block enabling sync for non-subscribers
        if (newValue && !isSubscribe) {
            toast.error(isAllowCloud.message || 'Upgrade to unlock cloud sync')
            setUpgradeModal({ isOpen: true, lastOpen: new Date() })
            return
        }
        onChange(newValue)
    }

    return (
        <div className={`flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 ${className || ''}`}>
            <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                    <BsCloudArrowUp className="text-green-600 dark:text-green-400 text-sm" />
                </div>
                <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Sync to cloud</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Backup & access anywhere</p>
                </div>
            </div>
            <SwitchToggle value={value} onChange={handleToggle} />
        </div>
    )
})

ChatSyncToCloudButton.displayName = 'ChatSyncToCloudButton'
